import {
  getBlob,
  getDownloadURL,
  getMetadata,
  ref,
  uploadBytes,
  type FirebaseStorage,
  type StorageReference,
  type UploadMetadata,
} from "firebase/storage";
import {
  getAttachmentConfig,
  type SmartTagAttachmentConfig,
} from "../../config/smartTagAttachments";
import type { SmartTagAttachmentKey } from "../../types/vcard";

const STATUS_PENDING = "pending";
const STATUS_CLEAN = "clean";
const STATUS_INFECTED = "infected";
const STATUS_ERROR = "error";
const SCAN_SOURCE = "client-upload";
const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 90 * 1000;

export const ATTACHMENT_PREFIX = "smarttag-attachments/";

const PROTECTED_PREFIXES = [
  "profile-pictures/",
  "vcard-photos/",
  ATTACHMENT_PREFIX,
];
const STORAGE_OBJECT_NOT_FOUND = "storage/object-not-found";

export type AttachmentScanStatus =
  | "pending"
  | "clean"
  | "infected"
  | "error"
  | "missing"
  | "unknown";

export type AttachmentErrorCode =
  | "unsupported_file_type"
  | "file_too_large"
  | "attachment_count_exceeded"
  | "storage_quota_exceeded"
  | "scan_rejected"
  | "scan_timed_out";

export class AttachmentValidationError extends Error {
  constructor(
    public readonly code: AttachmentErrorCode,
    message: string,
  ) {
    super(message);
    this.name = "AttachmentValidationError";
  }
}

const delay = (ms: number) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

const getFileParts = (fileName: string) => {
  const trimmed = fileName.trim();
  if (!trimmed) {
    return { baseName: "upload", extension: "" };
  }

  const extensionIndex = trimmed.lastIndexOf(".");
  if (extensionIndex <= 0) {
    return { baseName: trimmed, extension: "" };
  }

  return {
    baseName: trimmed.slice(0, extensionIndex),
    extension: trimmed.slice(extensionIndex).toLowerCase(),
  };
};

const sanitizePathSegment = (value: string, fallback: string): string => {
  const sanitized = value
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");

  return sanitized || fallback;
};

const requiresMalwareScan = (storagePath: string): boolean =>
  PROTECTED_PREFIXES.some((prefix) => storagePath.startsWith(prefix));

const shouldSkipPolling = (): boolean =>
  import.meta.env.DEV &&
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname);

const getOriginalFileName = (file: Blob): string | null =>
  file instanceof File && file.name.trim().length > 0 ? file.name : null;

const getPendingMetadata = (
  contentType: string,
  originalFileName?: string | null,
): UploadMetadata => ({
  contentType,
  customMetadata: {
    malwareScanSource: SCAN_SOURCE,
    malwareScanStatus: STATUS_PENDING,
    malwareScanRequestedAt: new Date().toISOString(),
    ...(originalFileName ? { originalFileName } : {}),
  },
});

const buildDeletedByScanError = (asset: string): AttachmentValidationError =>
  new AttachmentValidationError(
    "scan_rejected",
    `Upload was rejected during the malware scan. Please choose a different ${asset}.`,
  );

const buildTimedOutError = (asset: string): AttachmentValidationError =>
  new AttachmentValidationError(
    "scan_timed_out",
    `Upload is still being scanned. Please try again in a moment if the ${asset} does not appear.`,
  );

const formatMegabytes = (bytes: number): string => {
  const mb = bytes / (1024 * 1024);
  return mb < 10 ? `${mb.toFixed(1)}MB` : `${Math.round(mb)}MB`;
};

const isStorageObjectNotFoundError = (error: unknown): boolean => {
  if (typeof error !== "object" || error === null) {
    return false;
  }
  const code = (error as { code?: unknown }).code;
  return typeof code === "string" && code === STORAGE_OBJECT_NOT_FOUND;
};

export const buildProtectedUploadFileName = (
  fileName: string,
  fallbackBaseName = "upload",
): string => {
  const { baseName, extension } = getFileParts(fileName);
  const safeBaseName = sanitizePathSegment(baseName, fallbackBaseName);
  const safeExtension = extension
    ? `.${sanitizePathSegment(extension.slice(1), "bin")}`
    : "";

  return `${Date.now()}-${safeBaseName}${safeExtension}`;
};

const getScanStatus = async (
  storageRef: StorageReference,
): Promise<string | null> => {
  const metadata = await getMetadata(storageRef);
  return metadata.customMetadata?.malwareScanStatus ?? null;
};

// Polls the storage object's malwareScanStatus metadata until it is "clean".
// Throws on "infected"/"error", on the object disappearing (deleted by the
// scanner), and on timeout. Used by both image and document upload helpers.
const waitForCleanScan = async (
  storageRef: StorageReference,
  asset: string,
): Promise<void> => {
  const deadline = Date.now() + POLL_TIMEOUT_MS;

  while (Date.now() < deadline) {
    try {
      const scanStatus = await getScanStatus(storageRef);

      if (scanStatus === STATUS_CLEAN) {
        return;
      }

      if (scanStatus === STATUS_INFECTED || scanStatus === STATUS_ERROR) {
        throw buildDeletedByScanError(asset);
      }
    } catch (error) {
      if (isStorageObjectNotFoundError(error)) {
        throw buildDeletedByScanError(asset);
      }
      throw error;
    }

    await delay(POLL_INTERVAL_MS);
  }

  throw buildTimedOutError(asset);
};

export const uploadProtectedImage = async ({
  storage,
  storagePath,
  file,
}: {
  storage: FirebaseStorage;
  storagePath: string;
  file: Blob;
}): Promise<{ downloadUrl: string; storageRef: StorageReference }> => {
  const storageRef = ref(storage, storagePath);
  const metadata = getPendingMetadata(
    file.type || "application/octet-stream",
    getOriginalFileName(file),
  );

  await uploadBytes(storageRef, file, metadata);

  if (!requiresMalwareScan(storagePath) || shouldSkipPolling()) {
    const downloadUrl = await getDownloadURL(storageRef);
    return { downloadUrl, storageRef };
  }

  await waitForCleanScan(storageRef, "image");
  const downloadUrl = await getDownloadURL(storageRef);
  return { downloadUrl, storageRef };
};

// Sibling to uploadProtectedImage for document attachments. Same upload +
// pending-metadata + polling pattern, but accepts non-image MIMEs and uses
// document-flavored error copy. Callers should run validateAttachmentFile
// before invoking this. Persist only `storagePath` to Firestore — never the
// downloadUrl, which is intended for immediate-render only.
export const uploadProtectedFile = async ({
  storage,
  storagePath,
  file,
}: {
  storage: FirebaseStorage;
  storagePath: string;
  file: Blob;
}): Promise<{ downloadUrl: string; storageRef: StorageReference }> => {
  const storageRef = ref(storage, storagePath);
  const metadata = getPendingMetadata(
    file.type || "application/octet-stream",
    getOriginalFileName(file),
  );

  await uploadBytes(storageRef, file, metadata);

  if (!requiresMalwareScan(storagePath) || shouldSkipPolling()) {
    const downloadUrl = await getDownloadURL(storageRef);
    return { downloadUrl, storageRef };
  }

  await waitForCleanScan(storageRef, "document");
  const downloadUrl = await getDownloadURL(storageRef);
  return { downloadUrl, storageRef };
};

// Validates a file against the attachment registry's MIME and size policy
// for a given attachmentKey. Throws on invalid input. Use before calling
// uploadProtectedFile so we never write rejected files to Storage.
export const validateAttachmentFile = (
  file: File | Blob,
  attachmentKey: SmartTagAttachmentKey,
): void => {
  const config: SmartTagAttachmentConfig = getAttachmentConfig(attachmentKey);

  if (!config.allowedMimeTypes.includes(file.type)) {
    const fileType = file.type || "(unknown)";
    throw new AttachmentValidationError(
      "unsupported_file_type",
      `File type "${fileType}" is not allowed for ${config.label}. Accepted types: ${config.allowedMimeTypes.join(", ")}.`,
    );
  }

  if (file.size > config.maxSizeBytes) {
    throw new AttachmentValidationError(
      "file_too_large",
      `File is too large. Please choose a file smaller than ${formatMegabytes(config.maxSizeBytes)}.`,
    );
  }
};

// Reads the current malware scan status from a Storage object's metadata.
// Used at render time so the UI reflects authoritative server state without
// caching scanStatus in Firestore (PRD §7.4).
export const resolveAttachmentScanStatus = async (
  storageRef: StorageReference,
): Promise<AttachmentScanStatus> => {
  try {
    const status = await getScanStatus(storageRef);
    if (status === STATUS_PENDING) return "pending";
    if (status === STATUS_CLEAN) return "clean";
    if (status === STATUS_INFECTED) return "infected";
    if (status === STATUS_ERROR) return "error";
    return "unknown";
  } catch (error) {
    if (isStorageObjectNotFoundError(error)) {
      return "missing";
    }
    throw error;
  }
};

// Fetches an attachment's bytes from Storage as a Blob using authenticated
// SDK reads. Storage rules verify ownership at request time (PRD §13/§14),
// so a non-owner call rejects here, not at URL-issuance time. Callers
// typically wrap the returned Blob in `URL.createObjectURL(...)` for an
// in-tab Open action and revoke the object URL on unmount/teardown to
// release memory. Never persist a Firebase download URL — only `storagePath`
// should land in Firestore.
export const fetchAttachmentBlob = async (
  storageRef: StorageReference,
): Promise<Blob> => {
  return getBlob(storageRef);
};
